interface Point {
  x: number;
  y: number;
  label?: string;
  color?: 'coral' | 'violet' | 'sky' | 'sun';
}

interface Props {
  points?: Point[];
  xMin?: number;
  xMax?: number;
  yMin?: number;
  yMax?: number;
  title?: string;
  connectPoints?: boolean; // draw line segments between consecutive points (for polygons)
}

const colorMap = {
  coral: '#ff7a6b',
  violet: '#b693f6',
  sky: '#2b8bd8',
  sun: '#ffd166',
} as const;

// Coordinate plane — standard xy grid with axes, quadrant support, plot
// points, optionally connect them. For sections 4.4, 6.5, 7.4.
export default function CoordinatePlaneViz({
  points = [],
  xMin,
  xMax,
  yMin,
  yMax,
  title = 'Coordinate plane',
  connectPoints = false,
}: Props) {
  // Auto-range to include all points plus padding if bounds not given.
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const inferredXMin = xs.length ? Math.min(...xs) : -5;
  const inferredXMax = xs.length ? Math.max(...xs) : 5;
  const inferredYMin = ys.length ? Math.min(...ys) : -5;
  const inferredYMax = ys.length ? Math.max(...ys) : 5;

  const minX = xMin ?? Math.min(inferredXMin - 1, -5);
  const maxX = xMax ?? Math.max(inferredXMax + 1, 5);
  const minY = yMin ?? Math.min(inferredYMin - 1, -5);
  const maxY = yMax ?? Math.max(inferredYMax + 1, 5);

  const xRange = maxX - minX;
  const yRange = maxY - minY;

  const size = 280;
  const pad = 18;
  const inner = size - pad * 2;
  const xStep = inner / xRange;
  const yStep = inner / yRange;

  // Math (x, y) → SVG (svgX, svgY).  y is inverted because SVG grows downward.
  const toSvgX = (x: number) => pad + (x - minX) * xStep;
  const toSvgY = (y: number) => pad + (maxY - y) * yStep;

  // Integer gridlines
  const xTicks: number[] = [];
  for (let x = Math.ceil(minX); x <= Math.floor(maxX); x++) xTicks.push(x);
  const yTicks: number[] = [];
  for (let y = Math.ceil(minY); y <= Math.floor(maxY); y++) yTicks.push(y);

  const polyPoints = points.map((p) => `${toSvgX(p.x)},${toSvgY(p.y)}`).join(' ');

  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-display text-lg">{title}</h3>
      <div className="flex justify-center overflow-x-auto">
        <svg width="100%" viewBox={`0 0 ${size} ${size}`} style={{ maxWidth: size }}>
          {/* Minor gridlines */}
          {xTicks.map((x) => (
            <line
              key={`vx-${x}`}
              x1={toSvgX(x)}
              y1={pad}
              x2={toSvgX(x)}
              y2={size - pad}
              stroke="#e6ebf2"
              strokeWidth="0.5"
            />
          ))}
          {yTicks.map((y) => (
            <line
              key={`hy-${y}`}
              x1={pad}
              y1={toSvgY(y)}
              x2={size - pad}
              y2={toSvgY(y)}
              stroke="#e6ebf2"
              strokeWidth="0.5"
            />
          ))}

          {/* Axes */}
          <line x1={pad} y1={toSvgY(0)} x2={size - pad} y2={toSvgY(0)} stroke="#1b2a3a" strokeWidth="1" />
          <line x1={toSvgX(0)} y1={pad} x2={toSvgX(0)} y2={size - pad} stroke="#1b2a3a" strokeWidth="1" />

          {/* Axis labels */}
          <text
            x={size - pad + 2}
            y={toSvgY(0) - 4}
            fontSize="10"
            fontFamily="JetBrains Mono, monospace"
            fill="#6b7582"
          >
            x
          </text>
          <text
            x={toSvgX(0) + 4}
            y={pad + 8}
            fontSize="10"
            fontFamily="JetBrains Mono, monospace"
            fill="#6b7582"
          >
            y
          </text>

          {/* Tick numbers on axes (every 1 unit, small integers only) */}
          {xTicks.map((x) =>
            x !== 0 ? (
              <text
                key={`tx-${x}`}
                x={toSvgX(x)}
                y={toSvgY(0) + 12}
                fontSize="9"
                fontFamily="JetBrains Mono, monospace"
                textAnchor="middle"
                fill="#6b7582"
              >
                {x}
              </text>
            ) : null
          )}
          {yTicks.map((y) =>
            y !== 0 ? (
              <text
                key={`ty-${y}`}
                x={toSvgX(0) - 6}
                y={toSvgY(y) + 3}
                fontSize="9"
                fontFamily="JetBrains Mono, monospace"
                textAnchor="end"
                fill="#6b7582"
              >
                {y}
              </text>
            ) : null
          )}

          {/* Optional connecting lines (for polygons) */}
          {connectPoints && points.length >= 2 && (
            <polyline
              points={polyPoints + (points.length >= 3 ? ` ${toSvgX(points[0].x)},${toSvgY(points[0].y)}` : '')}
              fill="none"
              stroke="#b693f6"
              strokeWidth="2"
              strokeLinejoin="round"
              opacity="0.7"
            />
          )}

          {/* Points */}
          {points.map((p, i) => {
            const color = colorMap[p.color ?? 'coral'];
            return (
              <g key={`pt-${i}`}>
                <circle cx={toSvgX(p.x)} cy={toSvgY(p.y)} r={4} fill={color} />
                <text
                  x={toSvgX(p.x) + 7}
                  y={toSvgY(p.y) - 5}
                  fontSize="10"
                  fontFamily="JetBrains Mono, monospace"
                  fontWeight="700"
                  fill={color}
                >
                  {p.label ?? `(${p.x}, ${p.y})`}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
